import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import type {
  DistiListing,
  DistiListingPickResult,
  ExtractedSpecs,
  ProductCodeOption,
} from '@techgitter/contracts';
import { PRODUCT_CODE_DEFAULTS } from '@techgitter/contracts';
import { useDistiListingSearch } from '../../hooks/useDistiListingSearch';
import { useAppSettings } from '../../hooks/useAppSettings';
import {
  parseSpecPaste,
  scrapeListingSpec,
} from '../../services/distiListingsApi';
import { formatSpecSummary } from '../../utils/quoteOptionSpecs';

interface DistiListingSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (result: DistiListingPickResult) => void;
  /**
   * If set, scopes the initial search to listings tagged with this code as a
   * prefix (e.g. `HW.PC.Laptop`). The user can clear it to search globally.
   */
  initialProductCode?: string;
  /**
   * Quote-level GST display mode. The picker's sell-price column flips to
   * inc-GST when 'inc' so the tech sees what the customer will see, but the
   * `sellPriceExGst` carried onto the QuoteOption remains canonical ex-GST.
   */
  priceDisplayMode?: 'ex' | 'inc';
  /**
   * Quote-level markup (multiplier-as-fraction, e.g. 0.115 = 11.5%). Used by
   * the picker to compute each row's customer-facing sell price from
   * (cost + shipping). Defaults to 11.5% when omitted.
   */
  markup?: number;
}

const formatCurrency = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

/**
 * Default markup applied on top of (cost + shipping) to derive the
 * customer-facing sell price. Single source of truth for the picker —
 * change this constant to retune the markup. A future iteration may
 * make this per-product-code or per-customer; for now it's a flat 12%.
 */
const DEFAULT_MARKUP = 0.12;

// Product-code list now comes from admin-managed settings (`AppSettings.productCodes`)
// with `PRODUCT_CODE_DEFAULTS` as the cold-cache fallback. The picker scopes its
// disti-listing search to whichever code the tech selects (prefix match on
// `cr001_product_code`); an "Any" option (empty string) widens the search.

/**
 * Per-row pricing math. Pure — same inputs, same outputs. Pulled out so
 * the breakdown UI and the onPick payload share one calculation path
 * instead of computing twice.
 */
function computePricing(
  costExGst: number,
  shippingExGst: number,
  markup: number,
  priceUnknown: boolean,
): { totalCost: number; sellPrice: number; profit: number } {
  const totalCost = (costExGst || 0) + (shippingExGst || 0);
  // POA rows: cost is bogus (0 or sentinel-driven), so a markup on it
  // is meaningless. Surface 0s and let the picker UI tell the tech to
  // call the disti.
  if (priceUnknown) {
    return { totalCost, sellPrice: 0, profit: 0 };
  }
  const sellPrice = Math.round(totalCost * (1 + markup) * 100) / 100;
  const profit = Math.round((sellPrice - totalCost) * 100) / 100;
  return { totalCost, sellPrice, profit };
}

/**
 * Pretty-print a spec value for the labelled grid. Returns an em-dash
 * for nulls/zeroes/empty so the grid stays visually balanced when the
 * extractor missed a field.
 */
function specValue(value: string | number | boolean | null | undefined, suffix = ''): string {
  if (value === null || value === undefined || value === '' || value === false) {
    return '—';
  }
  if (value === true) return 'Yes';
  return `${value}${suffix}`;
}

/**
 * The spec grid surfaces every extracted field as a label/value pair
 * in a 2-column layout. Even fields the extractor missed render with
 * an em-dash so the tech can see at a glance what's known vs unknown.
 */
const SpecGrid: React.FC<{ specs: ExtractedSpecs }> = ({ specs }) => {
  const rows: Array<[string, string]> = [
    ['CPU', specValue(specs.cpu)],
    ['CPU vendor', specValue(specs.cpuFamily)],
    ['CPU series', specValue(specs.cpuSeries)],
    ['RAM', specs.ramGb ? `${specs.ramGb}GB${specs.ramType ? ' ' + specs.ramType : ''}` : '—'],
    [
      'Storage',
      specs.storageGb
        ? `${specs.storageGb >= 1000 ? specs.storageGb / 1000 + 'TB' : specs.storageGb + 'GB'}${specs.storageType ? ' ' + specs.storageType : ''}`
        : '—',
    ],
    ['Screen', specs.screenSizeIn ? `${specs.screenSizeIn}"${specs.resolution ? ' ' + specs.resolution : ''}` : '—'],
    ['Brightness', specValue(specs.brightnessNits, ' nits')],
    ['OS', specValue(specs.operatingSystem)],
    ['Warranty', specValue(specs.warrantyYears, ' years')],
    ['LTE', specValue(specs.hasLte)],
    ['WiFi', specValue(specs.hasWifi)],
    ['Touchscreen', specValue(specs.hasTouchscreen)],
    ['Fingerprint', specValue(specs.hasFingerprint)],
    ['Backlit KB', specValue(specs.hasBacklitKeyboard)],
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        rowGap: 0.25,
        columnGap: 2,
      }}
    >
      {rows.map(([label, value]) => (
        <Box
          key={label}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
        >
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

function stockChip(stock: number | null) {
  if (stock === null) {
    return <Chip label="Stock unknown" size="small" />;
  }
  if (stock <= 0) {
    return <Chip label="Out of stock" size="small" color="error" />;
  }
  return (
    <Chip
      label={`In stock (${stock})`}
      size="small"
      color="success"
      variant="outlined"
    />
  );
}

export const DistiListingSearchDialog: React.FC<
  DistiListingSearchDialogProps
> = ({
  open,
  onClose,
  onPick,
  initialProductCode,
  priceDisplayMode = 'ex',
  markup,
}) => {
  // Treat undefined/NaN/negative markup as "use the default" so the picker
  // remains usable from any caller that hasn't been updated yet.
  const effectiveMarkup =
    typeof markup === 'number' && Number.isFinite(markup) && markup >= 0
      ? markup
      : DEFAULT_MARKUP;
  const toDisplay = (exGst: number): number =>
    priceDisplayMode === 'inc' ? exGst * 1.1 : exGst;

  // Settings-driven product-code catalog (with cold-cache fallback to
  // the built-in defaults).
  const { settings } = useAppSettings();
  const productCodeOptions: ProductCodeOption[] =
    settings?.productCodes ?? PRODUCT_CODE_DEFAULTS;
  // Default scope: caller's hint > first catalog entry > "Any".
  const defaultProductCode =
    initialProductCode ?? productCodeOptions[0]?.code ?? '';

  const [query, setQuery] = useState('');
  const [productCode, setProductCode] = useState(defaultProductCode);
  const [inStockOnly, setInStockOnly] = useState(true);
  // Spec filters — applied client-side on top of the server search.
  // Multi-select sets for the checkbox groups; single-value strings for
  // the dropdowns. Empty set / empty string = "any".
  const [computerType, setComputerType] = useState<string>('');
  const [screenSize, setScreenSize] = useState<string>('');
  const [cpuBuckets, setCpuBuckets] = useState<Set<string>>(new Set());
  const [ramSizes, setRamSizes] = useState<Set<number>>(new Set());
  const [storageSizes, setStorageSizes] = useState<Set<number>>(new Set());
  // Default sort: cheapest first. Most pick decisions key off price
  // and the tech can flip to other modes via the dropdown.
  const [sortBy, setSortBy] = useState<
    '' | 'price-asc' | 'price-desc' | 'stock-desc'
  >('price-asc');
  // Per-row shipping override. Keyed by listing id; absent means "use
  // disti default" (or the row's own per-row shipping when set in the
  // feed). Stored as a string for clean controlled-input UX — empty
  // string clears the override.
  const [shippingOverrides, setShippingOverrides] = useState<Record<string, string>>({});

  // Reset filters only on the closed→open transition. Watching
  // `initialProductCode` here would wipe filters mid-session because
  // picking an item mutates the parent's option list (and therefore
  // the computed initialProductCode), causing this effect to re-fire
  // and clobber the tech's in-progress search.
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true;
      setProductCode(defaultProductCode);
      setQuery('');
      setInStockOnly(true);
      setComputerType('');
      setScreenSize('');
      setCpuBuckets(new Set());
      setRamSizes(new Set());
      setStorageSizes(new Set());
      setSortBy('price-asc');
      setShippingOverrides({});
    } else if (!open) {
      wasOpenRef.current = false;
    }
  }, [open, initialProductCode, defaultProductCode]);

  // Spec filters are pushed to the OData query so the server returns
  // only matching rows. Picking any chip on its own counts as a search
  // trigger — the hook fires a fetch even without a query / product
  // code. Sets need to be sorted before joining so cache equality holds
  // regardless of click order.
  const cpuBucketsArr = React.useMemo(() => [...cpuBuckets].sort(), [cpuBuckets]);
  const ramGbArr = React.useMemo(
    () => [...ramSizes].sort((a, b) => a - b),
    [ramSizes],
  );
  const storageGbArr = React.useMemo(
    () => [...storageSizes].sort((a, b) => a - b),
    [storageSizes],
  );

  const { results: rawResults, isLoading, error } = useDistiListingSearch({
    query,
    productCode: productCode || undefined,
    inStockOnly,
    computerType: computerType || undefined,
    screenSize: screenSize || undefined,
    cpuBuckets: cpuBucketsArr,
    ramGb: ramGbArr,
    storageGb: storageGbArr,
  });

  // Per-row scrape state. `loading` blocks the row's button + locks
  // pick-on-row-click until the response lands. `overrides` carries
  // the post-scrape `DistiListing` so the row re-renders with fresh
  // specs without a full search round-trip.
  const [scrapeLoading, setScrapeLoading] = useState<Set<string>>(new Set());
  const [scrapeOverrides, setScrapeOverrides] = useState<Record<string, DistiListing>>({});
  // Tracks listings the tech has already added to the quote within the
  // current picker session. The dialog stays open after a pick so
  // multiple items can be added back-to-back; these rows render as
  // disabled with an "Added" chip to prevent accidental dupes.
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  // Per-row paste state — open-textarea visibility, the in-progress
  // text the tech is editing, and a separate "saving" set keyed by
  // listing id while the parse round-trip is in flight.
  const [pasteOpenFor, setPasteOpenFor] = useState<Set<string>>(new Set());
  const [pasteText, setPasteText] = useState<Record<string, string>>({});
  const [pasteSaving, setPasteSaving] = useState<Set<string>>(new Set());

  // Reset scrape + paste state when the dialog reopens — otherwise
  // stale overrides / open paste boxes leak across picker sessions.
  React.useEffect(() => {
    if (open) {
      setScrapeLoading(new Set());
      setScrapeOverrides({});
      setPasteOpenFor(new Set());
      setPasteText({});
      setPasteSaving(new Set());
      setAddedIds(new Set());
    }
  }, [open]);

  // Sort fresh rows ahead of stale ones so the tech sees the most
  // current data first. Within each group the original feed order
  // (by listingSeenAt desc, applied server-side) is preserved.
  const results = React.useMemo(() => {
    return [...rawResults].sort((a, b) => {
      const aStale = a.stale ? 1 : 0;
      const bStale = b.stale ? 1 : 0;
      return aStale - bStale;
    });
  }, [rawResults]);

  // CPU vendor is now stored directly on `cpuFamily` (Intel / AMD /
  // Apple / Snapdragon) — the spec extractor handles the mapping at
  // ingest, so the picker no longer needs a client-side derivation.

  // Screen-size buckets — the bucket KEY goes to the server, which
  // resolves it to an OData range filter on `cr001_spec_screen_size_in`.
  // Keep these in sync with `DataverseQueries.DISTI_SCREEN_SIZE_BOUNDS`
  // (the python-side resolver).
  const SCREEN_SIZE_BUCKETS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'smaller', label: 'Everything smaller' },
    { value: '13',      label: '13"' },
    { value: '14',      label: '14"' },
    { value: '15-16',   label: '15–16"' },
    { value: 'bigger',  label: 'Everything bigger' },
  ];

  // CPU buckets — the bucket KEY goes to the server, which resolves it
  // to an OData filter on `cr001_spec_cpu_family` (+ `cr001_spec_cpu_series`
  // for the Intel tier buckets). Keep in sync with
  // `DataverseQueries.DISTI_CPU_BUCKETS`.
  const CPU_BUCKETS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'intel-5', label: 'Intel 5' },
    { value: 'intel-7', label: 'Intel 7' },
    { value: 'amd',     label: 'AMD' },
    { value: 'apple',   label: 'Apple' },
  ];

  // RAM / storage chip values: union of a static common-sizes list
  // (so the chips are always visible — picking one on an empty
  // dialog kicks off the server-side search) and any unusual sizes
  // present in the loaded results.
  const filterOptions = React.useMemo(() => {
    const COMMON_RAM_GB = [4, 8, 16, 32, 64];
    const COMMON_STORAGE_GB = [128, 256, 512, 1024, 2048, 4096];
    const ramSet = new Set<number>(COMMON_RAM_GB);
    const storageSet = new Set<number>(COMMON_STORAGE_GB);
    for (const r of results) {
      if (r.specs.ramGb) ramSet.add(r.specs.ramGb);
      if (r.specs.storageGb) storageSet.add(r.specs.storageGb);
    }
    return {
      ramSizes: [...ramSet].sort((a, b) => a - b),
      storageSizes: [...storageSet].sort((a, b) => a - b),
    };
  }, [results]);

  // Spec filters (computerType / screenSize / cpuBuckets / RAM /
  // storage) are now pushed to the server OData query, so `results`
  // already reflects them. The only remaining client-side concern is
  // the sort order (server returns by listingSeenAt; the picker lets
  // techs flip to price / stock).
  const filtered = React.useMemo(() => {
    if (!sortBy) return results;
    const sorted = [...results];
    if (sortBy === 'price-asc' || sortBy === 'price-desc') {
      const dir = sortBy === 'price-asc' ? 1 : -1;
      sorted.sort((a, b) => (a.costExGst - b.costExGst) * dir);
    } else if (sortBy === 'stock-desc') {
      // Treat null stock (feed doesn't carry it) as -1 so unknown-stock
      // rows sink below known-in-stock rows when sorting high-to-low.
      sorted.sort(
        (a, b) => (b.stockOnHand ?? -1) - (a.stockOnHand ?? -1),
      );
    }
    return sorted;
  }, [results, sortBy]);

  const formatStorage = (gb: number): string =>
    gb >= 1000 ? `${gb / 1000}TB` : `${gb}GB`;

  /**
   * Effective shipping for a listing row, applying override > per-row
   * feed shipping > disti default > 0 in that priority order. Pure
   * function of inputs so the pricing breakdown and the pick payload
   * stay aligned.
   */
  const effectiveShipping = (listing: DistiListing): number => {
    const override = shippingOverrides[listing.id];
    if (override !== undefined && override.trim() !== '') {
      const parsed = Number(override);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }
    if (listing.shipping > 0) return listing.shipping;
    if (listing.distiShippingDefaultExGst != null) {
      return listing.distiShippingDefaultExGst;
    }
    return 0;
  };

  const handlePick = (listing: DistiListing) => {
    const shippingExGst = effectiveShipping(listing);
    const { sellPrice, profit } = computePricing(
      listing.costExGst,
      shippingExGst,
      effectiveMarkup,
      listing.priceUnknown,
    );
    const pick: DistiListingPickResult = {
      distiId: listing.distiId,
      distiName: listing.distiName,
      distiSku: listing.distiSku,
      productCode: listing.productCode,
      manufacturer: listing.manufacturer,
      // Prefer the calculated description (cleaner customer-facing
      // form) over the raw feed description; fall back to the feed
      // string for legacy rows that haven't been backfilled yet.
      description: listing.calculatedDescription || listing.description,
      costExGst: listing.costExGst,
      rrpEx: listing.rrpEx,
      rrpInc: listing.rrpInc,
      priceUnknown: listing.priceUnknown,
      shippingExGst,
      sellPriceExGst: sellPrice,
      profitExGst: profit,
      markup: effectiveMarkup,
      distiUrl: listing.distiUrl,
      stockOnHand: listing.stockOnHand,
      specs: listing.specs,
      imageUrl: listing.imageUrl,
    };
    onPick(pick);
    setAddedIds((prev) => {
      const next = new Set(prev);
      next.add(listing.id);
      return next;
    });
  };

  /**
   * Resolve the listing the picker should render — the post-scrape
   * override when present, otherwise the search-results version. The
   * override carries fresh specs / specSource / scraped_at after a
   * successful URL scrape so the row reflects the new state without
   * a refresh of the search results.
   */
  const effectiveListing = (listing: DistiListing): DistiListing =>
    scrapeOverrides[listing.id] ?? listing;

  /**
   * Click handler for the per-row "Fetch full spec" button.
   *
   * The endpoint always returns 200 (even on `blocked`/`failed`) with
   * the post-write `listing` so we can swap it in place from a single
   * code path. Hard errors (404 listing-not-found, 422 disti not
   * opted in) throw — we surface those minimally; the UI gates the
   * button on the same flags so they shouldn't fire in practice.
   */
  const handleScrape = async (listingId: string) => {
    setScrapeLoading((prev) => {
      const next = new Set(prev);
      next.add(listingId);
      return next;
    });
    try {
      const result = await scrapeListingSpec(listingId);
      if (result.listing) {
        setScrapeOverrides((prev) => ({
          ...prev,
          [listingId]: result.listing!,
        }));
      }
    } catch (err) {
      // Pre-flight failures end up here. The picker doesn't have a
      // global error toast surface; log to console — the button is
      // gated on the same flags upstream so this is a defensive
      // fallback the tech shouldn't normally see.
      // eslint-disable-next-line no-console
      console.error('Spec scrape failed', err);
    } finally {
      setScrapeLoading((prev) => {
        const next = new Set(prev);
        next.delete(listingId);
        return next;
      });
    }
  };

  const togglePasteOpen = (listingId: string) => {
    setPasteOpenFor((prev) => {
      const next = new Set(prev);
      if (next.has(listingId)) {
        next.delete(listingId);
      } else {
        next.add(listingId);
      }
      return next;
    });
  };

  /**
   * Submit handler for the per-row "Apply" paste button.
   *
   * Uses the same row-replace pattern as `handleScrape` — the
   * post-write listing comes back in `result.listing` and replaces
   * the row in place. Empty pastes are no-op'd at the UI layer; the
   * backend also rejects them with 400.
   */
  const handleApplyPaste = async (listingId: string) => {
    const text = (pasteText[listingId] || '').trim();
    if (!text) return;
    setPasteSaving((prev) => {
      const next = new Set(prev);
      next.add(listingId);
      return next;
    });
    try {
      const result = await parseSpecPaste(listingId, text);
      if (result.listing) {
        setScrapeOverrides((prev) => ({
          ...prev,
          [listingId]: result.listing!,
        }));
      }
      // Close the textarea on success so the spec grid replaces it.
      // Failed parses keep it open so the tech can retry / edit.
      if (result.status === 'ok') {
        setPasteOpenFor((prev) => {
          const next = new Set(prev);
          next.delete(listingId);
          return next;
        });
        setPasteText((prev) => {
          const next = { ...prev };
          delete next[listingId];
          return next;
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Spec paste failed', err);
    } finally {
      setPasteSaving((prev) => {
        const next = new Set(prev);
        next.delete(listingId);
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Find disti listing
        {initialProductCode ? ` (scoped to ${initialProductCode})` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="product-code-label">Type</InputLabel>
            <Select
              labelId="product-code-label"
              label="Type"
              value={
                // Preserve unknown codes (e.g. legacy options scoped to a
                // bespoke productCode no longer in the catalog) by rendering
                // them as a disabled option so the search stays scoped
                // where the caller asked.
                productCodeOptions.some((o) => o.code === productCode)
                  ? productCode
                  : productCode || ''
              }
              onChange={(e) => setProductCode(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {productCodeOptions.map((o) => (
                <MenuItem key={o.code} value={o.code}>
                  {o.label}
                </MenuItem>
              ))}
              {productCode &&
                !productCodeOptions.some((o) => o.code === productCode) && (
                  <MenuItem value={productCode} disabled>
                    {productCode}
                  </MenuItem>
                )}
            </Select>
          </FormControl>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Search description, SKU, spec"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={inStockOnly}
                onChange={(e) => setInStockOnly(e.target.checked)}
              />
            }
            label="In stock only"
            sx={{ ml: 1, whiteSpace: 'nowrap' }}
          />
        </Stack>

        {/* Primary spec filters — surfaced at the top so the four
            most-used facets (Screen size / RAM / Storage / CPU) are
            one click away. Chip-group sections render only when the
            loaded results carry that spec; an empty dialog stays
            compact thanks to the `length > 0` guards below. */}
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Screen size</InputLabel>
            <Select
              label="Screen size"
              value={screenSize}
              onChange={(e) => setScreenSize(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {SCREEN_SIZE_BUCKETS.map((b) => (
                <MenuItem key={b.value} value={b.value}>
                  {b.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>CPU</InputLabel>
            <Select
              multiple
              label="CPU"
              value={[...cpuBuckets]}
              onChange={(e) => {
                const v = e.target.value;
                const arr = typeof v === 'string' ? v.split(',') : (v as string[]);
                setCpuBuckets(new Set(arr));
              }}
              renderValue={(selected) =>
                (selected as string[])
                  .map(
                    (key) =>
                      CPU_BUCKETS.find((b) => b.value === key)?.label ?? key,
                  )
                  .join(', ')
              }
            >
              {CPU_BUCKETS.map((b) => (
                <MenuItem key={b.value} value={b.value}>
                  <Checkbox size="small" checked={cpuBuckets.has(b.value)} />
                  <ListItemText primary={b.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>RAM</InputLabel>
            <Select
              multiple
              label="RAM"
              value={[...ramSizes]}
              onChange={(e) => {
                const v = e.target.value;
                const arr =
                  typeof v === 'string'
                    ? v.split(',').map(Number)
                    : (v as number[]);
                setRamSizes(new Set(arr));
              }}
              renderValue={(selected) =>
                (selected as number[])
                  .slice()
                  .sort((a, b) => a - b)
                  .map((n) => `${n}GB`)
                  .join(', ')
              }
            >
              {filterOptions.ramSizes.map((gb) => (
                <MenuItem key={gb} value={gb}>
                  <Checkbox size="small" checked={ramSizes.has(gb)} />
                  <ListItemText primary={`${gb}GB`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Storage</InputLabel>
            <Select
              multiple
              label="Storage"
              value={[...storageSizes]}
              onChange={(e) => {
                const v = e.target.value;
                const arr =
                  typeof v === 'string'
                    ? v.split(',').map(Number)
                    : (v as number[]);
                setStorageSizes(new Set(arr));
              }}
              renderValue={(selected) =>
                (selected as number[])
                  .slice()
                  .sort((a, b) => a - b)
                  .map(formatStorage)
                  .join(', ')
              }
            >
              {filterOptions.storageSizes.map((gb) => (
                <MenuItem key={gb} value={gb}>
                  <Checkbox size="small" checked={storageSizes.has(gb)} />
                  <ListItemText primary={formatStorage(gb)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Tertiary controls — Computer type + Sort by stay collapsed
            since techs touch them less often than the four spec
            facets above. */}
        <Accordion
          disableGutters
          elevation={0}
          sx={{ mb: 2, '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              More filters
              {(() => {
                const n = (computerType ? 1 : 0) + (sortBy ? 1 : 0);
                return n > 0 ? ` · ${n} active` : '';
              })()}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0.5, pt: 0 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Computer type</InputLabel>
                <Select
                  label="Computer type"
                  value={computerType}
                  onChange={(e) => setComputerType(e.target.value)}
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Laptop">Laptop</MenuItem>
                  <MenuItem value="Desktop">Desktop</MenuItem>
                  <MenuItem value="AIO">AIO</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={sortBy}
                  onChange={(e) =>
                    setSortBy(e.target.value as typeof sortBy)
                  }
                >
                  <MenuItem value="">Default (fresh first)</MenuItem>
                  <MenuItem value="price-asc">Price: low → high</MenuItem>
                  <MenuItem value="price-desc">Price: high → low</MenuItem>
                  <MenuItem value="stock-desc">Stock: high → low</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Searching…
            </Typography>
          </Box>
        )}
        {!isLoading && filtered.length === 0 && (query || productCode) && (
          <Typography variant="body2" color="text.secondary">
            {results.length === 0
              ? 'No matches found.'
              : 'No results match the current filters.'}
          </Typography>
        )}
        <List dense>
          {filtered.map((rawListing) => {
            // After a successful URL scrape, render the post-scrape
            // listing so updated specs / calculated description /
            // specSource flow through to the row immediately. Picks
            // also use this — the option lands on the quote with the
            // freshly-scraped specs.
            const listing = effectiveListing(rawListing);
            const isScraping = scrapeLoading.has(listing.id);
            const isAdded = addedIds.has(listing.id);
            return (
            <ListItemButton
              key={listing.id}
              onClick={() => {
                if (isScraping || isAdded) return;
                handlePick(listing);
              }}
              disabled={isAdded}
              alignItems="flex-start"
              sx={isAdded ? { opacity: 0.55 } : undefined}
            >
              {/*
                Always-visible thumbnail slot. The wrapper Box always
                renders at 64x64 so rows align consistently — when no
                imageUrl is set, or the disti hotlink-blocks / 404s
                (img onError hides itself), the placeholder icon shows
                through. `referrerPolicy="no-referrer"` is a
                belt-and-braces hint to disti CDNs.
              */}
              <Box
                sx={{
                  position: 'relative',
                  flex: '0 0 auto',
                  width: 64,
                  height: 64,
                  mr: 1.5,
                  mt: 0.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.paper',
                }}
              >
                <ImageOutlinedIcon
                  sx={{ color: 'action.disabled', fontSize: 32 }}
                />
                {listing.imageUrl && (
                  <Box
                    component="img"
                    src={listing.imageUrl}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      bgcolor: 'background.paper',
                    }}
                  />
                )}
              </Box>
              <ListItemText
                disableTypography
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {listing.description || '(no description)'}
                    </Typography>
                    {listing.manufacturer && (
                      <Chip
                        label={listing.manufacturer}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {listing.productCode && (
                      <Chip
                        label={listing.productCode}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {listing.priceUnknown && (
                      <Chip
                        label="POA"
                        size="small"
                        color="warning"
                      />
                    )}
                    {listing.stale && (
                      <Chip
                        label="Stale"
                        size="small"
                        title="Not seen in the most recent feed pull — price/stock may be out of date"
                        sx={{ bgcolor: 'grey.300', color: 'text.secondary' }}
                      />
                    )}
                    {stockChip(listing.stockOnHand)}
                    {isAdded && (
                      <Chip
                        label="Added to quote"
                        size="small"
                        color="success"
                        variant="filled"
                      />
                    )}
                  </Stack>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {listing.priceUnknown ? (
                          <>
                            {listing.distiName} · SKU {listing.distiSku} ·{' '}
                            <strong>Price on application</strong> — call{' '}
                            {listing.distiName}
                          </>
                        ) : (
                          <>
                            {listing.distiName} · SKU {listing.distiSku} · cost{' '}
                            {formatCurrency(listing.costExGst)}
                            {listing.rrpInc > 0
                              ? ` · RRP ${formatCurrency(listing.rrpInc)} inc`
                              : ''}
                            {listing.shipping
                              ? ` + ${formatCurrency(listing.shipping)} shipping`
                              : ''}
                          </>
                        )}
                      </Typography>
                      {listing.distiUrl && (
                        <Link
                          href={listing.distiUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Open
                        </Link>
                      )}
                    </Stack>
                    {(() => {
                      const summary = formatSpecSummary(listing.specs);
                      return summary ? (
                        <Typography
                          variant="caption"
                          color="text.primary"
                          sx={{ display: 'block', mt: 0.25 }}
                        >
                          {summary}
                        </Typography>
                      ) : null;
                    })()}
                    {(() => {
                      const shipping = effectiveShipping(listing);
                      const { totalCost, sellPrice, profit } = computePricing(
                        listing.costExGst,
                        shipping,
                        effectiveMarkup,
                        listing.priceUnknown,
                      );
                      const sellDisplay = toDisplay(sellPrice);
                      const profitDisplay = toDisplay(profit);
                      const priceSuffix = priceDisplayMode === 'inc' ? 'inc' : 'ex';
                      return (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                          }}
                          // The override TextField sits inside a
                          // ListItemButton; we stop propagation here so
                          // editing shipping doesn't immediately fire
                          // handlePick. The rest of the row stays
                          // clickable for the pick action.
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography variant="caption" color="text.secondary">
                              Cost {formatCurrency(listing.costExGst)}
                            </Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                + Shipping
                              </Typography>
                              <TextField
                                size="small"
                                value={
                                  shippingOverrides[listing.id] ??
                                  String(shipping)
                                }
                                onChange={(e) =>
                                  setShippingOverrides((prev) => ({
                                    ...prev,
                                    [listing.id]: e.target.value,
                                  }))
                                }
                                inputMode="decimal"
                                inputProps={{
                                  style: {
                                    width: 60,
                                    padding: '2px 6px',
                                    fontSize: '0.75rem',
                                    textAlign: 'right',
                                  },
                                }}
                                sx={{ '& .MuiInputBase-root': { borderRadius: 1 } }}
                              />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              = Total {formatCurrency(totalCost)}
                            </Typography>
                            {listing.priceUnknown ? (
                              <Typography variant="caption" color="warning.main">
                                Sell — call disti
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="caption" fontWeight="bold">
                                  Sell {formatCurrency(sellDisplay)} {priceSuffix}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="success.main"
                                >
                                  Markup {formatCurrency(profitDisplay)} {priceSuffix}
                                </Typography>
                              </>
                            )}
                            {listing.distiDeliveryDays != null && (
                              <Typography variant="caption" color="text.secondary">
                                · ~{listing.distiDeliveryDays}d delivery
                              </Typography>
                            )}
                          </Stack>
                          {listing.distiShippingNotes && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                            >
                              {listing.distiShippingNotes}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                    <Accordion
                      // Stop click propagation so toggling the accordion
                      // doesn't immediately fire the row's pick handler.
                      onClick={(e) => e.stopPropagation()}
                      disableGutters
                      elevation={0}
                      sx={{
                        mt: 0.5,
                        bgcolor: 'transparent',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="small" />}
                        sx={{
                          minHeight: 28,
                          px: 0.5,
                          '& .MuiAccordionSummary-content': { my: 0.25 },
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Specs &amp; quote preview
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                        <SpecGrid specs={listing.specs} />
                        {(() => {
                          const isPasteOpen = pasteOpenFor.has(listing.id);
                          const isPasteSaving = pasteSaving.has(listing.id);
                          const hasExtractedSource =
                            listing.specSource === 'url_jsonld' ||
                            listing.specSource === 'url_html' ||
                            listing.specSource === 'manual';
                          return (
                            <>
                              <Box
                                sx={{
                                  mt: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                {listing.distiUrlScrapeEnabled && listing.distiUrl && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={isScraping}
                                    startIcon={
                                      isScraping ? (
                                        <CircularProgress size={14} />
                                      ) : undefined
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleScrape(listing.id);
                                    }}
                                  >
                                    {isScraping
                                      ? 'Fetching…'
                                      : listing.specSource === 'url_jsonld' ||
                                        listing.specSource === 'url_html'
                                      ? 'Re-fetch specs'
                                      : 'Fetch full spec'}
                                  </Button>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePasteOpen(listing.id);
                                  }}
                                >
                                  {isPasteOpen
                                    ? 'Cancel paste'
                                    : listing.specSource === 'manual'
                                    ? 'Re-paste specs'
                                    : 'Paste specs'}
                                </Button>
                                {hasExtractedSource && !isPasteOpen ? (
                                  <Typography variant="caption" color="text.secondary">
                                    Specs {listing.specSource === 'manual' ? 'pasted' : 'fetched'}
                                    {listing.specScrapedAt
                                      ? ` ${new Date(listing.specScrapedAt).toLocaleString('en-AU')}`
                                      : ''}
                                    {' · '}source: {listing.specSource}
                                  </Typography>
                                ) : listing.specSource === 'blocked' ? (
                                  <Chip
                                    label={`Couldn't reach ${listing.distiName}`}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    title={listing.specScrapeError ?? undefined}
                                  />
                                ) : listing.specSource === 'failed' ? (
                                  <Chip
                                    label="No specs found"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    title={listing.specScrapeError ?? undefined}
                                  />
                                ) : null}
                              </Box>
                              {isPasteOpen && (
                                <Box
                                  // Stop propagation so typing / clicking
                                  // inside the paste UI doesn't pick the
                                  // row.
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ mt: 1 }}
                                >
                                  <TextField
                                    multiline
                                    minRows={4}
                                    maxRows={12}
                                    fullWidth
                                    size="small"
                                    placeholder={
                                      'Paste the spec block from the supplier or manufacturer page.\n\nE.g.:\nProcessor\tIntel Core Ultra 5 125H\nMemory\t16 GB DDR5-5600\nStorage\t512 GB PCIe NVMe SSD\nOperating System\tWindows 11 Pro'
                                    }
                                    value={pasteText[listing.id] ?? ''}
                                    onChange={(e) =>
                                      setPasteText((prev) => ({
                                        ...prev,
                                        [listing.id]: e.target.value,
                                      }))
                                    }
                                    autoFocus
                                  />
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 0.5 }}
                                  >
                                    <Button
                                      size="small"
                                      variant="contained"
                                      disabled={
                                        isPasteSaving ||
                                        !(pasteText[listing.id] || '').trim()
                                      }
                                      startIcon={
                                        isPasteSaving ? (
                                          <CircularProgress size={14} />
                                        ) : undefined
                                      }
                                      onClick={() => handleApplyPaste(listing.id)}
                                    >
                                      {isPasteSaving ? 'Parsing…' : 'Apply'}
                                    </Button>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ alignSelf: 'center' }}
                                    >
                                      We'll extract CPU / RAM / storage / OS /
                                      screen / etc. from the pasted text and
                                      regenerate the quote description.
                                    </Typography>
                                  </Stack>
                                </Box>
                              )}
                            </>
                          );
                        })()}
                        <Box sx={{ mt: 1.5 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.25 }}
                          >
                            Quote will say:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              p: 0.75,
                              bgcolor: 'background.paper',
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              fontStyle: listing.calculatedDescription
                                ? 'normal'
                                : 'italic',
                              color: listing.calculatedDescription
                                ? 'text.primary'
                                : 'text.secondary',
                            }}
                          >
                            {listing.calculatedDescription ||
                              `${listing.description} — (no calculated description yet; the feed line will be used)`}
                          </Typography>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                }
              />
            </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {addedIds.size > 0
            ? `Back to quote (${addedIds.size} added)`
            : 'Back to quote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
